import { FunctionComponent, useState } from 'react'
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@black-bear-energy/rucksack'
import {
  ChecklistDocument,
  ChecklistDocumentSummary,
} from '@black-bear-energy/black-bear-energy-common'
import { ExpandMore } from '@mui/icons-material'
import { ChecklistMenu, ChecklistMenuProps } from './ChecklistMenu'
import { InlineFileUploader } from './InlineFileUploader'
import { DateAndTime } from './DateAndTime'
import { ReviewsIcon } from './ReviewsIcon'
import { RequiredDocumentsProgressBar } from './RequiredDocumentsProgressBar'
import { checklistChildIsChecklist } from '../helpers/DocumentExchange/checklistChildIsDocument'
import { getSearchFormattedText } from '../helpers/getSearchFormattedText'
import { DocumentSection } from './DocumentSection'
import {
  isBBEUserOrSuperAdmin,
  useImpersonatingUserOrUser,
} from '../../state/modules/auth'
import {
  CHECKLIST_TABLE_WIDTH,
  CHECKLIST_COLUMN_WIDTHS,
} from '../helpers/DocumentExchange/tableConstants'
import { DocumentStatusChip } from './DocumentStatusChip'

export const ChecklistSection: FunctionComponent<ChecklistSectionProps> = ({
  checklistSummary,
  isLast,
  searchString,
  isNested,
}: ChecklistSectionProps) => {
  // If true, the checklist section is nested within a parent checklist section.
  const nested = isNested === true
  // Default top-level checklists to closed and nested checklists to open
  const [toggleOpen, setToggleOpen] = useState(nested)

  // expand the section if the user is searching
  const open = toggleOpen || !!searchString

  const user = useImpersonatingUserOrUser()
  const isLimitedUser = !!user && isBBEUserOrSuperAdmin(user) == false

  /**
   * Flattens a checklist into the list of upload targets offered by the checklist upload
   * modal. A nested document collection appears as a single entry named after the
   * collection. Selecting this adds a new document to the collection.
   */
  function getDocChildren(
    checklist: ChecklistDocumentSummary
  ): { id: number; name: string }[] {
    return checklist.children.flatMap((child) => {
      if (!checklistChildIsChecklist(child)) {
        return child
      }
      if (isDocumentCollection(child)) {
        return { id: child.placeholderDocumentId, name: child.checklistName }
      }
      return getDocChildren(child)
    })
  }

  const documentChildren = getDocChildren(checklistSummary)
  const isCollection = isDocumentCollection(checklistSummary)
  const checklistMenuProps: ChecklistMenuProps = isCollection
    ? {
        checklistName: checklistSummary.checklistName,
        documents: documentChildren,
        isCollectionUpload: true,
        placeholderDocumentId: checklistSummary.placeholderDocumentId,
      }
    : {
        checklistName: checklistSummary.checklistName,
        documents: documentChildren,
        isCollectionUpload: false,
      }

  return (
    <>
      <TableRow
        data-testid="checklistSection"
        style={{
          backgroundColor: 'rgba(32, 43, 57, 0.04)',
        }}
        sx={
          isLast && !open
            ? { '& .MuiTableCell-root': { borderBottom: 'none' } }
            : undefined
        }
      >
        <TableCell style={nested ? { paddingLeft: '40px' } : {}}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              sx={{ width: '40px', height: '40px' }}
              onClick={() => setToggleOpen(!toggleOpen)}
            >
              <ExpandMore
                sx={{
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </IconButton>
            <Typography variant="subtitle1" maxWidth="336px">
              {getSearchFormattedText(
                checklistSummary.checklistName,
                searchString
              )}
            </Typography>
          </Box>
        </TableCell>
        <TableCell style={nested ? { paddingLeft: '40px' } : {}}>
          <Box maxWidth="136px" padding="6px 0">
            <RequiredDocumentsProgressBar
              required={checklistSummary.required}
            />
          </Box>
        </TableCell>
        <TableCell />
        <TableCell style={{ padding: '5px 20px' }}>
          <ReviewsIcon
            verified={checklistSummary.verified}
            pendingReview={checklistSummary.pendingReview}
          />
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell>
          <Box>
            {checklistSummary.updatedBy && (
              <DateAndTime date={new Date(checklistSummary.lastUpdated)} />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Box>
            {/* The three dot menu is shown to BBE users for every checklist (bulk upload), and to
                all users for a document collection, since adding a document to a collection happens
                from this menu. */}
            {(isLimitedUser === false || isCollection) && (
              <ChecklistMenu {...checklistMenuProps} />
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={CHECKLIST_COLUMN_WIDTHS.length}
          style={{ padding: 0, border: 0 }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table
              sx={{
                tableLayout: 'fixed',
                width: CHECKLIST_TABLE_WIDTH,
                minWidth: '1000px',
              }}
            >
              <colgroup>
                {CHECKLIST_COLUMN_WIDTHS.map((width, index) => (
                  <col style={{ width }} key={index} />
                ))}
              </colgroup>
              <TableBody>
                {isCollection && checklistSummary.children.length === 0 ? (
                  <TableRow sx={{ position: 'relative' }}>
                    <TableCell
                      style={
                        nested
                          ? { padding: '8px 0px 8px 40px' }
                          : { padding: '8px 8px' }
                      }
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{ width: '40px', height: '40px', flexShrink: 0 }}
                        />
                        <Typography variant="body1" fontStyle="italic">
                          No Uploads
                        </Typography>
                        <InlineFileUploader
                          documentId={checklistSummary.placeholderDocumentId}
                          documentName={checklistSummary.checklistName}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {checklistSummary.required.denominator > 0 && (
                        <Box
                          display="flex"
                          alignItems="flex-start"
                          paddingLeft="16px"
                        >
                          <DocumentStatusChip
                            document={
                              {
                                uploaded: false,
                                required: true,
                              } as ChecklistDocument
                            }
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell colSpan={CHECKLIST_COLUMN_WIDTHS.length - 2} />
                  </TableRow>
                ) : (
                  checklistSummary.children.map((child, index) => {
                    if (checklistChildIsChecklist(child)) {
                      return (
                        <ChecklistSection
                          key={index}
                          checklistSummary={child}
                          isNested={true}
                          searchString={searchString}
                        />
                      )
                    } else {
                      return (
                        <DocumentSection
                          key={index}
                          document={child}
                          isLimitedUser={isLimitedUser}
                          isNested={nested}
                          searchString={searchString}
                          isCollectionDocument={isCollection}
                        />
                      )
                    }
                  })
                )}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

interface ChecklistSectionProps {
  checklistSummary: ChecklistDocumentSummary
  isLast?: boolean
  searchString?: string
  isNested?: boolean
}

/**
 * Narrows a checklist summary to a document collection rather than a standard checklist. A document
 * collection has a `placeholderDocumentId` which identifies the slot the next upload fills. A null
 * placeholder marks a standard checklist.
 */
function isDocumentCollection(
  summary: ChecklistDocumentSummary
): summary is ChecklistDocumentSummary & { placeholderDocumentId: number } {
  return summary.placeholderDocumentId !== null
}
